import React from "react";
import { createPortal } from "react-dom";
import "./NotificationsOffPopup.css";
import "./InstantUnavailablePopup.css";
import { t, MessageKey } from "../i18n";
import type { InstantExplainer } from "../utils/instantUnavailable";

// "Why can't I pick Instant?" (founder, TestFlight 1.5.2): what a tap on the
// grey Instant row in the Transfer sheet opens when instant is NOT AVAILABLE.
// One title, one plain line — why, and what to do instead — then "Got it".
// Two messages: "for this amount" and "temporarily unavailable" (which
// reason is which: utils/instantUnavailable). No debit card is a different
// screen: the card-art "Get paid in ~30 minutes" popup (InstantPayoutsPopup
// needsCard), the same one Balance opens — one screen for "no card".
// Same card as that popup (.notifoff-*). Portaled to <body> with the popup's
// z-index, so it sits over the Transfer sheet (z 1001).

type Variant = Exclude<InstantExplainer, "needsCard">;

const COPY: Record<Variant, { title: MessageKey; sub: MessageKey }> = {
  amount: { title: "instantWhy.amount.title", sub: "instantWhy.amount.sub" },
  unavailable: { title: "instantWhy.unavailable.title", sub: "instantWhy.unavailable.sub" },
};

const ClockIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);
const InfoIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5M12 8h.01" />
  </svg>
);

const ICON: Record<Variant, () => React.ReactElement> = {
  amount: ClockIcon,
  unavailable: InfoIcon,
};

type Props = {
  variant: Variant;
  onClose: () => void;
};

export default function InstantUnavailablePopup({ variant, onClose }: Props) {
  const Icon = ICON[variant];
  return createPortal(
    <div
      className="notifoff-overlay ixw"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ixw-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="notifoff-card" data-variant={variant}>
        <div className="ixw-icon" aria-hidden="true">
          <Icon />
        </div>
        <div className="notifoff-title" id="ixw-title">
          {t(COPY[variant].title)}
        </div>
        <p className="notifoff-sub">{t(COPY[variant].sub)}</p>
        <button type="button" className="notifoff-cta" onClick={onClose}>
          {t("instantPayoutsPopup.gotIt")}
        </button>
      </div>
    </div>,
    document.body
  );
}
